import { CounterWithIdentitiesType, DocumentType, TelecomType } from '..';
import { UserSettings } from '../auth/user.type';

import { Address } from './address';
import { Attachment } from './attachment';
import { DocumentSource } from './documentSource.types';
import { Integration } from './integration.types';
import { Organization } from './organization';
import { Patient, PatientVerification } from './patient.types';
import { Practitioner } from './practitioner';
import { PractitionerRole } from './practitionerRole';
import { Professional, ProfessionalBase, ProfessionalDto } from './professional';
import {
  DocumentRecipientBundle,
  DocumentRecipientDto,
  MediumList,
  RecipientContext,
} from './recipient';
import { Telecom } from './telecom';

export enum DocumentJobError {
  WrongFileType = 'WRONG_FILE_TYPE',
  FileSizeTooBig = 'FILE_SIZE_TOO_BIG',
  TooManyPages = 'TOO_MANY_PAGES',
  IsNotA4 = 'IS_NOT_A4',
  IsNotA1 = 'IS_NOT_A1',
  CorruptedFile = 'CORRUPTED_FILE',
  UploadFailed = 'UPLOAD_FAILED',
  NetworkError = 'NETWORK_ERROR',
  SuspiciousFile = 'SUSPICIOUS_FILE',
  Default = 'DEFAULT',
}

export enum DocumentJobStatus {
  Pending = 'PENDING' /* Job created. Waiting to be processed */,
  Processing = 'PROCESSING' /* Job processing. Job is uploading */,
  UserCancelled = 'USER_CANCELLED' /* Cancelled by user before upload */,
  Failed = 'FAILED' /* Invalid format, or failed to create resource on server */,
  Processed = 'PROCESSED' /* Successfully created on server */,
}

export type UploadSource = 'opening' | 'watcher' | 'button' | 'drop';

export type FlowType = 'integration' | 'sending';

/**
 * The result of a file being uploaded by the user,
 * generates a Document when status is 'PROCESSED'
 */
export type UploadDocumentJob = {
  status: DocumentJobStatus;
  uploadSource: UploadSource;
  startDate: Date;
  sender: Professional;
  id: string;
  filePath: string;
  fileStorageId: string;
  fileName: string;
  errors?: DocumentJobError[];
  documentId?: SendingRequest['id'];
  flowType: FlowType;
  batchUploadId?: string;
  /** Wall-clock start of the batch session; set with batchUploadId for accurate batch durationMs. */
  batchStartDate?: Date;
  /** Total files in the batch session; used to emit batch end across partial queue drains. */
  batchDocumentsCount?: number;
};

/** Batch correlation fields propagated on upload jobs and Redux upload actions. */
export type BatchUploadMetadata = Pick<
  UploadDocumentJob,
  'batchUploadId' | 'batchStartDate' | 'batchDocumentsCount'
>;

export enum DocumentStatus {
  Draft = 'draft',
  Active = 'active',
  Suspended = 'suspended',
  Cancelled = 'cancelled',
  Completed = 'completed',
  EnteredInError = 'entered-in-error',
}

/**
 * A set of tags that will impact a Document's
 * processing throughout Lifen systems
 */

// TODO move all tag handling to server side
export enum DocumentTag {
  ActivatedByDesktop = 'DESKTOP-APP',
  ActivatedByWeb = 'WEB-APP',
  DematPatientSendingProhibited = 'DEMAT_PATIENT_SENDING_PROHIBITED',
  DematPatientWithout2faProhibited = 'DEMAT_PATIENT_WITHOUT_2FA_PROHIBITED',
  Desktop = 'DESKTOP',
  ExtractedWithoutOCR = 'EXTRACTED_WITHOUT_OCR',
  ImmediateMode = 'IMMEDIATE_MODE',
  Import = 'IMPORT',
  InternalSendingProhibited = 'INTERNAL_SENDING_PROHIBITED',
  InternalSendingProhibitedIfPostal = 'INTERNAL_SENDING_PROHIBITED_IF_POSTAL',
  MainRecipientOnly = 'MAIN_RECIPIENT_ONLY',
  NeedsOcr = 'NEEDS_OCR',
  NeedSplit = 'NEED_SPLIT',
  NoPatientResend = 'NONE',
  PatientResendIfBounce = 'BOUNCE',
  PatientResendIfBounceOrUnread = 'BOUNCE_OR_UNREAD',
  PatientSendingProhibited = 'PATIENT_SENDING_PROHIBITED',
  ProcessedByIrene = 'PROCESSED',
  ProcessingOcr = 'PROCESSING_OCR',
  RecipientVerified = 'RECIPIENT_VERIFIED',
  SenderVerified = 'SENDER_VERIFIED',
  SendingProhibited = 'SENDING_PROHIBITED',
  V5 = 'V5',
  DmpUnavailable = 'DMP_UNAVAILABLE',
}

/**
 * Whether the document requires additionnal
 * verification from the user
 */

export enum DocumentVerificationStatus {
  ReadyToSend = 'READY_TO_SEND',
  UserVerificationNeeded = 'USER_VERIFICATION_NEEDED',
  UserCorrectionNeeded = 'USER_CORRECTION_NEEDED',
  Waiting = 'WAITING',
  Sending = 'SENDING',
  Downgraded = 'DOWNGRADED_MODE',
}

export enum DocumentVerificationTypes {
  ai_not_available = 'ai-not-available',
  dmp_not_available = 'dmp-not-available',
  enrichment_failed = 'enrichment-failed',
  invalid_patient_name = 'invalid-patient-name',
  integration_missing = 'integration-missing',
  patient_missing = 'patient-missing',
  professional_recipients_missing = 'professional-recipients-missing',
  recipient_patient_minor = 'recipient-minor',
  recipients_missing = 'recipients-missing',
  recipients_other_than_high = 'recipients-other-than-high',
  recipient_certainly_not = 'recipient-certainly-not',
  search_gateway_not_available = 'search-gateway-not-available', // should be on patient
  document_type_other_than_high = 'document-type-other-than-high',
  document_speciality_other_than_high = 'document-speciality-other-than-high',
  document_split_suggested = 'document-split-suggested',
  attachment_with_more_than_40_pages = 'attachment-with-more-than-40-pages',
  recipient_context = 'recipient-context',
}

export type DocumentVerification =
  | DocumentVerificationTypes.ai_not_available
  | DocumentVerificationTypes.dmp_not_available
  | DocumentVerificationTypes.enrichment_failed
  | DocumentVerificationTypes.invalid_patient_name
  | DocumentVerificationTypes.integration_missing
  | DocumentVerificationTypes.patient_missing // not use anymore (except in server)
  | DocumentVerificationTypes.professional_recipients_missing
  | DocumentVerificationTypes.recipients_missing
  | DocumentVerificationTypes.recipient_patient_minor
  | DocumentVerificationTypes.recipients_other_than_high
  | DocumentVerificationTypes.recipient_certainly_not
  | DocumentVerificationTypes.search_gateway_not_available
  | DocumentVerificationTypes.document_type_other_than_high
  | DocumentVerificationTypes.document_speciality_other_than_high
  | DocumentVerificationTypes.document_split_suggested
  | DocumentVerificationTypes.attachment_with_more_than_40_pages
  | DocumentVerificationTypes.recipient_context;

export type VerifiableType = 'Document' | 'Patient' | 'Recipient'; // Prediction? // use common type with recipients
export interface DocumentVerificationV2 {
  id: number;
  key: DocumentVerificationTypes;
  verifiableId: number;
  verifiableType: VerifiableType;
  dismissedAt?: string;
  recipientContext?: RecipientContext;
  /**
   * Whether the user may wave this verification away, straight off the server's
   * `Verifications::VERIFICATIONS`. Read it rather than keeping a list of keys client-side.
   *
   * Independent of whether the verification blocks: `document_type_other_than_high` is both, so it
   * raises the document to `USER_VERIFICATION_NEEDED` and is still the user's to dismiss.
   *
   * Served by `VerificationBlueprint`. Absent on the payloads rendered by
   * `Api::V2::VerificationBlueprint` — the sending side — which does not carry the field, hence the
   * optional mark; treat absent as "not known to be dismissable".
   */
  isDismissable?: boolean;
}

/**
 * Confidence level from AI (Irene)
 */
export type PredictionCertainty = 'possible' | 'probable' | 'certain';

export interface PatientPrediction {
  id: Patient['id'];
  predictionCertainty: PredictionCertainty;
}

export type RecipientId = ProfessionalBase['id'] | Patient['id'];

export interface RecipientPrediction {
  id: RecipientId;
  predictionCertainty?: PredictionCertainty;
}

export interface DocumentPatientDto {
  id: Patient['id'];
  ehrPatientId?: Patient['id'];
  telecoms: MediumList<Telecom>;
  addresses: MediumList<Address>;
  verifications: PatientVerification[];
}

export type DocumentPatient = Omit<DocumentPatientDto, 'id' | 'ehrPatientId'> & {
  patient: Patient;
  ehrPatient?: Patient;
};

export interface DocumentBase {
  authoredOn: string;
  endPeriod?: string;
  id: string;
  versionId: string;
  integration: Integration | undefined;
  requesterId: string;
  startPeriod?: string;
  status: DocumentStatus;
  supportReference: string;
  documentDuplicated?: string;
  isReadOnly?: boolean;
}

/**
 * Created from a `DocumentJob` after it has been processed.
 */
export interface SendingRequestBase extends DocumentBase {
  documentType?: DocumentType;
  documentSpeciality?: string;
  documentsMerged?: string[];
  documentReplaced?: string;
  predictions?: {
    moreSuggestedRecipients?: boolean;
  };
  hasBeenForwarded?: boolean;
  resendOriginalTelecomType?: TelecomType;
  resendSourceReference?: string;
  resendTrigger?: 'unread' | 'delivery_feedback';
  received?: string;
  message?: { subject?: string; html?: string; plain?: string };
  isSplit?: boolean;
  isSliced?: boolean;
  isReadOnly: boolean;
  verificationsV2: DocumentVerificationV2[];
  verificationStatus: DocumentVerificationStatus;
}

export interface SendingRequest extends SendingRequestBase {
  documentType?: DocumentType;
  documentSpeciality?: string;
  sender: Professional;
  patient?: DocumentPatient;
  recipients: DocumentRecipientBundle[];
  source?: DocumentSource;
}

export interface SendingRequestDto extends SendingRequestBase {
  sender: ProfessionalDto;
  patient?: DocumentPatientDto;
  recipients: DocumentRecipientDto[];
  source?: DocumentSource;
}

export type NewDocumentPayload = Omit<
  SendingRequestBase,
  | 'authoredOn'
  | 'documentType'
  | 'documentSpeciality'
  | 'id'
  | 'integration'
  | 'status'
  | 'supportReference'
  | 'verificationStatus'
  | 'verifications'
  | 'verificationsV2'
  | 'versionId'
> & {
  sender: Professional;
  file: File;
  flowType: FlowType;
  /**
   * Monkeypath to fix https://honestica.atlassian.net/browse/CORE-3149
   * @todo please delete this after the creation of alphonse /v1/users/{id}/settings endpoint
   * don't forget to delete userSettings property in packages/server/api/validators/schemas/Document.json
   */
  userSettings: UserSettings;
};

export interface SendingRequestBundle {
  document: SendingRequestDto;
  attachments?: Attachment[]; // TODO: delete
  entities: {
    patients: Patient[];
    organizations: Organization[];
    practitioners: Practitioner[];
    practitionerRoles: PractitionerRole[];
    attachments?: Attachment[];
  };
}

export enum SendingsDashboardType {
  Draft = 'draft',
  Inbox = 'inbox',
  Sent = 'sent',
  Archive = 'archive',
}

export const isSendingDashboardType = (val: unknown): val is SendingsDashboardType =>
  typeof val === 'string' &&
  Object.values(SendingsDashboardType).includes(val as SendingsDashboardType);

export const isSendingRequestDto = (val: unknown): val is SendingRequestDto =>
  (val as SendingRequest)?.recipients !== undefined;

export type FhirEntries = {
  patients: Patient[];
  practitioners: Practitioner[];
  organizations: Organization[];
};

export type CounterReadyToSend = CounterWithIdentitiesType;
export type CounterDraft = CounterWithIdentitiesType;
